import { useGetList, type Identifier } from "ra-core";
import { TASK_STATUS_FILTERS } from "@/components/atomic-crm/tasks/taskConstants";
import type {
  Company,
  Contact,
  ContactNote,
  Deal,
} from "@/components/atomic-crm/types";
import type { FormSubmissionV2 } from "@/modules/forms/types";
import type {
  ClientInvoice,
  Contract,
  Proposal,
  Ticket,
} from "@/modules/types";
import {
  CONTACT_STATUS_FILTER,
  LEAD_STATUS_FILTER,
} from "@/modules/shared/relatedFilters";
import { useConfigurationContext } from "@/components/atomic-crm/root/ConfigurationContext";
import { buildOpenDealsFilter } from "@/modules/deals/openDealFilters";

const countQuery = (_resource: string, filter: Record<string, unknown>) => ({
  filter,
  pagination: { page: 1, perPage: 1 },
  sort: { field: "id", order: "DESC" as const },
});

export const useClientTabCounts = (companyId: Company["id"] | "") => {
  const { dealPipelineStatuses, dealStages } = useConfigurationContext();
  const enabled = companyId !== "" && companyId != null;
  const staleTime = 30_000;

  const { total: contacts = 0 } = useGetList<Contact>(
    "contacts",
    countQuery("contacts", {
      "company_id@eq": companyId,
      "status@in": CONTACT_STATUS_FILTER,
    }),
    { staleTime, enabled },
  );
  const { total: leads = 0 } = useGetList<Contact>(
    "contacts",
    countQuery("contacts", {
      "company_id@eq": companyId,
      "status@in": LEAD_STATUS_FILTER,
    }),
    { staleTime, enabled },
  );
  const { total: projects = 0 } = useGetList<Deal>(
    "deals",
    countQuery("deals", { "company_id@eq": companyId }),
    { staleTime, enabled },
  );
  const { total: deals = 0 } = useGetList<Deal>(
    "deals",
    countQuery(
      "deals",
      buildOpenDealsFilter(dealPipelineStatuses, dealStages, {
        "company_id@eq": companyId,
      }),
    ),
    { staleTime, enabled },
  );
  const { total: proposals = 0 } = useGetList<Proposal>(
    "proposals",
    countQuery("proposals", { "company_id@eq": companyId }),
    { staleTime, enabled },
  );
  const { total: contracts = 0 } = useGetList<Contract>(
    "contracts",
    countQuery("contracts", { "company_id@eq": companyId }),
    { staleTime, enabled },
  );
  const { total: invoices = 0 } = useGetList<ClientInvoice>(
    "client_invoices",
    countQuery("client_invoices", { "company_id@eq": companyId }),
    { staleTime, enabled },
  );
  const { total: tickets = 0 } = useGetList<Ticket>(
    "tickets",
    countQuery("tickets", { "company_id@eq": companyId }),
    { staleTime, enabled },
  );
  const { total: webForms = 0 } = useGetList<FormSubmissionV2>(
    "form_submissions_v2",
    countQuery("form_submissions_v2", { "company_id@eq": companyId }),
    { staleTime, enabled },
  );
  const { total: referrals = 0 } = useGetList<Contact>(
    "contacts",
    countQuery("contacts", { "referred_by_company_id@eq": companyId }),
    { staleTime, enabled },
  );

  const { data: companyDeals = [] } = useGetList<Deal>(
    "deals",
    {
      filter: { "company_id@eq": companyId },
      pagination: { page: 1, perPage: 200 },
      sort: { field: "id", order: "ASC" },
    },
    { staleTime, enabled },
  );

  const dealIds = companyDeals.map((deal) => deal.id);
  const paymentsFilter =
    dealIds.length > 0
      ? { "deal_id@in": `(${dealIds.join(",")})` }
      : { "deal_id@eq": -1 };

  const { total: invoicePayments = 0 } = useGetList<ClientInvoice>(
    "client_invoices",
    countQuery("client_invoices", {
      "company_id@eq": companyId,
      "@or": {
        "amount_paid@gt": 0,
        "status@eq": "paid",
      },
    }),
    { staleTime, enabled },
  );

  const { total: projectPayments = 0 } = useGetList(
    "deal_client_payments",
    countQuery("deal_client_payments", paymentsFilter),
    { staleTime, enabled: enabled && dealIds.length > 0 },
  );

  const { data: companyContacts = [] } = useGetList<Contact>(
    "contacts",
    {
      filter: { "company_id@eq": companyId },
      pagination: { page: 1, perPage: 200 },
      sort: { field: "id", order: "ASC" },
    },
    { staleTime, enabled },
  );

  const contactIds = companyContacts.map((contact) => contact.id);
  const contactFilter =
    contactIds.length > 0
      ? { "contact_id@in": `(${contactIds.join(",")})` }
      : { "contact_id@eq": -1 };

  const { total: tasks = 0 } = useGetList(
    "tasks",
    countQuery("tasks", { ...contactFilter, ...TASK_STATUS_FILTERS.open }),
    { staleTime, enabled: enabled && contactIds.length > 0 },
  );
  const { total: notes = 0 } = useGetList(
    "contact_notes",
    countQuery("contact_notes", { ...contactFilter, "parent_id@is": null }),
    { staleTime, enabled: enabled && contactIds.length > 0 },
  );

  const todayKey = new Date().toISOString().slice(0, 10);
  const { total: overdueTasks = 0 } = useGetList(
    "tasks",
    countQuery("tasks", {
      ...contactFilter,
      ...TASK_STATUS_FILTERS.open,
      "due_date@lt": todayKey,
    }),
    { staleTime, enabled: enabled && contactIds.length > 0 },
  );

  const { data: latestNotes = [] } = useGetList<ContactNote>(
    "contact_notes",
    {
      filter: contactFilter,
      pagination: { page: 1, perPage: 1 },
      sort: { field: "date", order: "DESC" as const },
    },
    { staleTime, enabled: enabled && contactIds.length > 0 },
  );

  return {
    contacts,
    deals,
    leads,
    projects,
    proposals,
    contracts,
    invoices,
    tickets,
    tasks,
    notes,
    webForms,
    payments: projectPayments + invoicePayments,
    referrals,
    overdueTasks,
    lastActivityAt: latestNotes[0]?.date ?? null,
    contactIds: contactIds as Identifier[],
  };
};
